import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { OrderEditForm } from "@/components/orders/OrderEditForm";
import { OrderLineItems } from "@/components/orders/OrderLineItems";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { ErrorState } from "@/components/shared/error-state";
import { StatusBadge } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeleteOrder, useOrder, useUpdateOrder } from "@/hooks/use-orders";
import { formatCurrency, formatDate } from "@/lib/format";

export function OrderDetailPage() {
  const { orderId } = useParams({ strict: false }) as { orderId: string };
  const navigate = useNavigate();
  const { data: order, isLoading, isError, error, refetch } = useOrder(orderId);
  const updateOrder = useUpdateOrder(orderId);
  const deleteOrder = useDeleteOrder();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load order"} onRetry={() => refetch()} />
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Order not found.</p>
        <Link to="/dashboard/orders" className="text-sm text-primary hover:underline mt-2 block">
          Back to orders
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link
          to="/dashboard/orders"
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Orders
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-2xl font-bold tracking-tight">Order for {order.customerName}</h1>
        {!editing && <StatusBadge status={order.status} />}
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Details</CardTitle>
          {!editing && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <OrderEditForm
              status={order.status}
              onSave={(values) =>
                updateOrder.mutate(values, { onSuccess: () => setEditing(false) })
              }
              onCancel={() => setEditing(false)}
              isSaving={updateOrder.isPending}
            />
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Customer</span>
                <p className="mt-1">
                  <Link
                    to="/dashboard/customers/$customerId"
                    params={{ customerId: order.customerId }}
                    className="text-primary hover:underline"
                  >
                    {order.customerName}
                  </Link>
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Status</span>
                <div className="mt-1">
                  <StatusBadge status={order.status} />
                </div>
              </div>
              <div>
                <span className="text-muted-foreground">Total</span>
                <p className="mt-1 font-medium">{formatCurrency(order.totalCents)}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Created</span>
                <p className="mt-1">{formatDate(order.createdAt)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <OrderLineItems
            orderId={order.id}
            lineItems={order.lineItems}
            totalCents={order.totalCents}
          />
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Delete order"
        description={`Delete this order for ${order.customerName}? Its line items will be removed too. This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleteOrder.isPending}
        onConfirm={() =>
          deleteOrder.mutate(order.id, {
            onSuccess: () => navigate({ to: "/dashboard/orders" }),
          })
        }
      />
    </div>
  );
}
